import { useState } from 'react';
// react-router-dom not needed for navigation - Stripe handles redirect
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { showError } from '@/lib/toast';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Cloud,
  Cpu,
  Server,
  Key,
  CreditCard,
  Loader2,
  Sparkles,
  Coins,
} from 'lucide-react';
import { channelIcons, aiModelIcons } from '@/components/icons/ChannelIcons';
import { useQuery } from '@tanstack/react-query';

const STEPS = ['type', 'configure', 'review'] as const;

interface WizardState {
  hostingType: 'managed' | 'byos' | '';
  keyMode: 'own_key' | 'platform_key';
  instanceName: string;
  aiModel: string;
  aiApiKey: string;
  gatewayPassword: string;
  channels: string[];
  paymentMethod: 'wallet' | 'stripe';
  // BYOS fields
  sshHost: string;
  sshPort: string;
  sshUser: string;
  sshKey: string;
}

export function SetupWizard() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [deploying, setDeploying] = useState(false);
  const [state, setState] = useState<WizardState>({
    hostingType: '',
    keyMode: 'own_key',
    instanceName: '',
    aiModel: '',
    aiApiKey: '',
    gatewayPassword: '',
    channels: [],
    paymentMethod: 'stripe',
    sshHost: '',
    sshPort: '22',
    sshUser: 'root',
    sshKey: '',
  });

  const { data: aiModels } = useQuery({
    queryKey: ['ai-models'],
    queryFn: async () => {
      const { data } = await api.get('/providers/config/ai-models');
      return data;
    },
  });

  const { data: channelsList } = useQuery({
    queryKey: ['channels'],
    queryFn: async () => {
      const { data } = await api.get('/providers/config/channels');
      return data;
    },
  });

  const { data: walletBalance } = useQuery({
    queryKey: ['wallet-balance'],
    queryFn: async () => {
      const { data } = await api.get('/wallet/balance');
      return data;
    },
  });

  const { data: pricing } = useQuery({
    queryKey: ['pricing'],
    queryFn: async () => {
      const { data } = await api.get('/billing/pricing');
      return data;
    },
  });

  const stepLabels = [t('setup.steps.type'), t('setup.steps.configure'), t('setup.steps.review')];

  const canProceed = () => {
    switch (STEPS[currentStep]) {
      case 'type':
        return !!state.hostingType;
      case 'configure': {
        const baseOk = !!state.instanceName && !!state.aiModel;
        if (state.hostingType === 'byos') {
          return baseOk && !!state.sshHost && !!state.sshKey && !!state.aiApiKey;
        }
        // Managed + own_key requires the user's API key
        if (state.hostingType === 'managed' && !isPlatformKey) {
          return baseOk && !!state.aiApiKey;
        }
        return baseOk;
      }
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const isPlatformKey = state.keyMode === 'platform_key';

  // Get pricing from API or use fallback
  const byosPrice = pricing?.byos?.priceOneTime || 10;
  const managedPrice = pricing?.managed?.priceMonthly || 10;

  const priceLabel = state.hostingType === 'byos' ? `$${byosPrice}` : `$${managedPrice}`;
  const priceType = state.hostingType === 'byos'
    ? t('landing.pricing.oneTime')
    : '/mo';
  const priceSuffix = isPlatformKey ? ` ${t('landing.pricing.plusFunds')}` : '';

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      // For platform_key, use OpenRouter model ID; for own_key, use direct provider model ID
      const selectedModel = aiModels?.find((m: any) => m.id === state.aiModel);
      const aiModelId = isPlatformKey && selectedModel?.openrouterId
        ? selectedModel.openrouterId
        : state.aiModel;

      // If paying from wallet, use a different endpoint
      if (state.paymentMethod === 'wallet') {
        const { data } = await api.post('/billing/instance-checkout-wallet', {
          hostingType: state.hostingType,
          keyMode: state.keyMode,
          name: state.instanceName,
          aiModel: aiModelId,
          aiApiKey: isPlatformKey ? undefined : (state.aiApiKey || undefined),
          gatewayPassword: state.gatewayPassword || undefined,
          channels: state.channels,
          ...(state.hostingType === 'byos' && {
            sshHost: state.sshHost,
            sshPort: parseInt(state.sshPort) || 22,
            sshUser: state.sshUser || 'root',
            sshKey: state.sshKey,
          }),
        });

        // Redirect to instance page or deploy progress
        if (data.instanceId) {
          window.location.href = `/dashboard/instances/${data.instanceId}`;
        }
      } else {
        // Stripe checkout flow
        const { data } = await api.post('/billing/instance-checkout', {
          hostingType: state.hostingType,
          keyMode: state.keyMode,
          name: state.instanceName,
          aiModel: aiModelId,
          aiApiKey: isPlatformKey ? undefined : (state.aiApiKey || undefined),
          gatewayPassword: state.gatewayPassword || undefined,
          channels: state.channels,
          ...(state.hostingType === 'byos' && {
            sshHost: state.sshHost,
            sshPort: parseInt(state.sshPort) || 22,
            sshUser: state.sshUser || 'root',
            sshKey: state.sshKey,
          }),
        });
        if (data.url) {
          window.location.href = data.url;
        }
      }
    } catch (error: any) {
      showError(error.response?.data?.message || t('common.error'));
    } finally {
      setDeploying(false);
    }
  };

  const toggleChannel = (id: string) => {
    setState((prev) => ({
      ...prev,
      channels: prev.channels.includes(id)
        ? prev.channels.filter((c) => c !== id)
        : [...prev.channels, id],
    }));
  };

  const selectType = (hostingType: 'managed' | 'byos', keyMode: 'own_key' | 'platform_key') => {
    setState((prev) => ({ ...prev, hostingType, keyMode }));
  };

  // Get estimated monthly cost for the selected model in platform_key mode
  const selectedModelPricing = aiModels?.find((m: any) => m.id === state.aiModel)?.platformPricing;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold">{t('setup.title')}</h1>
        <p className="text-muted-foreground mt-1">
          {t('setup.subtitle')}
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((step, i) => (
          <div key={step} className="flex items-center gap-2">
            <div
              className={cn(
                'flex items-center justify-center h-8 w-8 rounded-full text-xs font-medium transition-colors',
                i < currentStep
                  ? 'gradient-primary text-white'
                  : i === currentStep
                    ? 'border-2 border-primary text-primary'
                    : 'border-2 border-muted text-muted-foreground',
              )}
            >
              {i < currentStep ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={cn('text-sm hidden sm:block', i === currentStep ? 'font-medium' : 'text-muted-foreground')}>
              {stepLabels[i]}
            </span>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-border" />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {/* Step 1: Choose Instance Type */}
          {STEPS[currentStep] === 'type' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">
                  {t('setup.chooseType')}
                </h2>
                <p className="text-muted-foreground">
                  {t('setup.chooseTypeSub')}
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {/* BYOS */}
                <Card
                  className={cn(
                    'cursor-pointer transition-all hover:shadow-md',
                    state.hostingType === 'byos' ? 'border-primary ring-2 ring-primary/20' : '',
                  )}
                  onClick={() => selectType('byos', 'own_key')}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-lg bg-orange-500/10">
                        <Server className="h-6 w-6 text-orange-500" />
                      </div>
                      {state.hostingType === 'byos' && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <h3 className="text-lg font-bold mb-1">
                      {t('setup.byosTitle')}
                    </h3>
                    <p className="text-3xl font-bold text-primary mb-2">${byosPrice}<span className="text-sm font-normal text-muted-foreground"> {t('landing.pricing.oneTime')}</span></p>
                    <p className="text-sm text-muted-foreground">
                      {t('setup.byosDescription')}
                    </p>
                  </CardContent>
                </Card>

                {/* Managed + Own Key */}
                <Card
                  className={cn(
                    'cursor-pointer transition-all hover:shadow-md',
                    state.hostingType === 'managed' && state.keyMode === 'own_key' ? 'border-primary ring-2 ring-primary/20' : '',
                  )}
                  onClick={() => selectType('managed', 'own_key')}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Cloud className="h-6 w-6 text-primary" />
                      </div>
                      {state.hostingType === 'managed' && state.keyMode === 'own_key' && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <h3 className="text-lg font-bold mb-1">
                      {t('setup.managedTitle')}
                    </h3>
                    <p className="text-3xl font-bold text-primary mb-2">${managedPrice}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground">
                      {t('setup.managedDescription')}
                    </p>
                  </CardContent>
                </Card>

                {/* Managed + Platform Key (Easiest) */}
                <Card
                  className={cn(
                    'cursor-pointer transition-all hover:shadow-md relative',
                    state.hostingType === 'managed' && state.keyMode === 'platform_key'
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-emerald-500/30',
                  )}
                  onClick={() => selectType('managed', 'platform_key')}
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="gradient-primary text-white border-0 shadow-sm">
                      <Sparkles className="h-3 w-3 mr-1" />
                      {t('setup.easiest')}
                    </Badge>
                  </div>
                  <CardContent className="p-6 pt-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-lg bg-emerald-500/10">
                        <Coins className="h-6 w-6 text-emerald-500" />
                      </div>
                      {state.hostingType === 'managed' && state.keyMode === 'platform_key' && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <h3 className="text-lg font-bold mb-1">
                      {t('setup.managedWalletTitle')}
                    </h3>
                    <p className="text-3xl font-bold text-primary mb-2">${managedPrice}<span className="text-sm font-normal text-muted-foreground">/mo {t('landing.pricing.plusFunds')}</span></p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {t('setup.managedWalletDescription')}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 2: Configure */}
          {STEPS[currentStep] === 'configure' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">
                  {t('setup.configureTitle')}
                </h2>
              </div>

              {/* Platform Key badge */}
              {isPlatformKey && (
                <div className="flex items-center gap-3 p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
                  <Coins className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">
                      {t('wallet.poweredByWallet')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('wallet.poweredByWalletDescription')}
                    </p>
                  </div>
                </div>
              )}

              {/* BYOS SSH fields */}
              {state.hostingType === 'byos' && (
                <Card>
                  <CardContent className="p-6 space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      {t('setup.sshConnection')}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('setup.host')}</Label>
                        <Input
                          value={state.sshHost}
                          onChange={(e) => setState((prev) => ({ ...prev, sshHost: e.target.value }))}
                          placeholder="89.167.79.11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('setup.port')}</Label>
                        <Input
                          type="number"
                          value={state.sshPort}
                          onChange={(e) => setState((prev) => ({ ...prev, sshPort: e.target.value }))}
                          placeholder="22"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('setup.username')}</Label>
                        <Input
                          value={state.sshUser}
                          onChange={(e) => setState((prev) => ({ ...prev, sshUser: e.target.value }))}
                          placeholder="root"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('setup.sshPrivateKey')}</Label>
                      <textarea
                        className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                        value={state.sshKey}
                        onChange={(e) => setState((prev) => ({ ...prev, sshKey: e.target.value }))}
                        placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Instance name */}
              <div className="space-y-2">
                <Label>{t('setup.instanceName')}</Label>
                <Input
                  value={state.instanceName}
                  onChange={(e) => setState((prev) => ({ ...prev, instanceName: e.target.value }))}
                  placeholder={t('setup.instanceNamePlaceholder')}
                />
              </div>

              {/* AI Model */}
              <div className="space-y-2">
                <Label>{t('setup.aiModel')}</Label>
                <div className="grid grid-cols-2 gap-4">
                  {aiModels?.map((model: any) => (
                    <Card
                      key={model.id}
                      className={cn(
                        'cursor-pointer transition-all hover:shadow-md',
                        state.aiModel === model.id ? 'border-primary ring-2 ring-primary/20' : '',
                      )}
                      onClick={() => setState((prev) => ({ ...prev, aiModel: model.id }))}
                    >
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {aiModelIcons[model.id] || <Cpu className="h-6 w-6 text-primary" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{model.name}</p>
                          <p className="text-xs text-muted-foreground">{model.description}</p>
                          {isPlatformKey && model.platformPricing && (
                            <p className="text-xs text-emerald-600 mt-1">
                              ~${(model.platformPricing.estimatedMonthlyCentsLight / 100).toFixed(0)}-${(model.platformPricing.estimatedMonthlyCentsMedium / 100).toFixed(0)}/mo
                            </p>
                          )}
                        </div>
                        {state.aiModel === model.id && (
                          <Check className="h-4 w-4 text-primary ml-auto flex-shrink-0" />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* API Key - only shown for own_key mode */}
              {!isPlatformKey && state.aiModel && (
                <div className="space-y-2">
                  <Label>{t('setup.aiApiKey')}</Label>
                  <Input
                    type="password"
                    value={state.aiApiKey}
                    onChange={(e) => setState((prev) => ({ ...prev, aiApiKey: e.target.value }))}
                    placeholder="sk-..."
                  />
                  <p className="text-xs text-muted-foreground">
                    {state.hostingType === 'managed'
                      ? t('setup.apiKeyRequired')
                      : t('setup.apiKeyEncrypted')}
                  </p>
                </div>
              )}

              {/* Channels - Optional */}
              <div className="space-y-2">
                <Label>{t('setup.channelsLabel')} <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                <p className="text-xs text-muted-foreground mb-3">
                  You can skip this for now and configure channels later through OpenClaw's Control UI after deployment.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {channelsList?.map((channel: any) => (
                    <button
                      key={channel.id}
                      onClick={() => toggleChannel(channel.id)}
                      className={cn(
                        'flex flex-col items-center gap-2 p-3 rounded-lg border transition-all text-sm',
                        state.channels.includes(channel.id)
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-accent',
                      )}
                    >
                      {channelIcons[channel.id] || <div className="h-6 w-6" />}
                      <span className="font-medium">{channel.name}</span>
                      {state.channels.includes(channel.id) && (
                        <Check className="h-3 w-3 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review & Pay */}
          {STEPS[currentStep] === 'review' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">
                  {t('setup.reviewTitle')}
                </h2>
                <p className="text-muted-foreground">
                  {t('setup.reviewSubtitle')}
                </p>
              </div>
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">{t('setup.hosting')}</span>
                    <span className="font-medium capitalize">{state.hostingType === 'byos' ? 'BYOS' : 'Managed'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">{t('setup.apiKeyMode')}</span>
                    <span className="font-medium">
                      {isPlatformKey ? (
                        <Badge variant="secondary" className="text-emerald-600 bg-emerald-500/10">
                          <Coins className="h-3 w-3 mr-1" />
                          {t('wallet.platformKey')}
                        </Badge>
                      ) : (
                        t('wallet.ownKey')
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">{t('setup.instanceName')}</span>
                    <span className="font-medium">{state.instanceName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">{t('setup.aiModel')}</span>
                    <span className="font-medium capitalize">{state.aiModel}</span>
                  </div>
                  {state.channels.length > 0 && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">{t('setup.channelsLabel')}</span>
                      <span className="font-medium">{state.channels.join(', ')}</span>
                    </div>
                  )}
                  {state.hostingType === 'byos' && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">{t('setup.review.server')}</span>
                      <span className="font-medium">{state.sshHost}:{state.sshPort}</span>
                    </div>
                  )}

                  {/* Wallet balance for platform_key */}
                  {isPlatformKey && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">{t('setup.walletBalance')}</span>
                      <span className="font-medium">
                        ${((walletBalance?.balanceCents || 0) / 100).toFixed(2)}
                        {(walletBalance?.balanceCents || 0) === 0 && (
                          <span className="text-xs text-amber-600 ml-2">
                            {t('wallet.fundsNeededAfterDeploy')}
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Estimated model cost for platform_key */}
                  {isPlatformKey && selectedModelPricing && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">{t('wallet.estimatedAiUsage')}</span>
                      <span className="text-sm text-muted-foreground">
                        ~${(selectedModelPricing.estimatedMonthlyCentsLight / 100).toFixed(0)}-${(selectedModelPricing.estimatedMonthlyCentsMedium / 100).toFixed(0)}/mo
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment Method Selection */}
              <Card>
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    {t('setup.paymentMethod')}
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Pay from Wallet */}
                    <Card
                      className={cn(
                        'cursor-pointer transition-all hover:shadow-md',
                        state.paymentMethod === 'wallet' ? 'border-primary ring-2 ring-primary/20' : '',
                      )}
                      onClick={() => setState((prev) => ({ ...prev, paymentMethod: 'wallet' }))}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="p-2 rounded-lg bg-emerald-500/10">
                            <Coins className="h-5 w-5 text-emerald-500" />
                          </div>
                          {state.paymentMethod === 'wallet' && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <h4 className="font-semibold text-sm mb-1">
                          {t('setup.payFromWallet')}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-2">
                          {t('setup.payFromWalletDescription')}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">{t('wallet.balance')}:</span>
                          <span className={cn(
                            "font-semibold text-sm",
                            (walletBalance?.balanceCents || 0) >= parseInt(priceLabel.replace('$', '')) * 100
                              ? "text-emerald-600"
                              : "text-amber-600"
                          )}>
                            ${((walletBalance?.balanceCents || 0) / 100).toFixed(2)}
                          </span>
                        </div>
                        {(walletBalance?.balanceCents || 0) < parseInt(priceLabel.replace('$', '')) * 100 && (
                          <p className="text-xs text-amber-600 mt-1">
                            {t('wallet.insufficientFunds')}
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Pay with Stripe */}
                    <Card
                      className={cn(
                        'cursor-pointer transition-all hover:shadow-md',
                        state.paymentMethod === 'stripe' ? 'border-primary ring-2 ring-primary/20' : '',
                      )}
                      onClick={() => setState((prev) => ({ ...prev, paymentMethod: 'stripe' }))}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <CreditCard className="h-5 w-5 text-primary" />
                          </div>
                          {state.paymentMethod === 'stripe' && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <h4 className="font-semibold text-sm mb-1">
                          {t('setup.payWithStripe')}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {t('setup.payWithStripeDescription')}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Total Cost */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between py-3 bg-muted/50 rounded-lg px-4">
                    <span className="font-semibold">
                      {state.hostingType === 'byos'
                        ? t('setup.setupFee')
                        : t('setup.monthlyCost')}
                    </span>
                    <span className="font-bold text-primary text-lg">
                      {priceLabel} {priceType}{priceSuffix}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => setCurrentStep((prev) => Math.max(0, prev - 1))}
          disabled={currentStep === 0}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </Button>

        {currentStep < STEPS.length - 1 ? (
          <Button
            onClick={() => setCurrentStep((prev) => prev + 1)}
            disabled={!canProceed()}
            className="gap-2"
          >
            {t('common.next')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={handleDeploy}
            disabled={deploying || (state.paymentMethod === 'wallet' && (walletBalance?.balanceCents || 0) < parseInt(priceLabel.replace('$', '')) * 100)}
            className="gap-2"
          >
            {deploying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('setup.processing')}
              </>
            ) : (
              <>
                {state.paymentMethod === 'wallet' ? (
                  <>
                    <Coins className="h-4 w-4" />
                    {`${t('setup.deductAndDeploy')} (${priceLabel} ${priceType}${priceSuffix})`}
                    {(walletBalance?.balanceCents || 0) > 0 && (
                      <span className="text-xs ml-1 opacity-80">
                        • ${((walletBalance?.balanceCents || 0) / 100).toFixed(2)} {t('wallet.available')}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    {`${t('setup.payAndDeploy')} (${priceLabel} ${priceType}${priceSuffix})`}
                  </>
                )}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
